import { Box } from "@mui/material";
import { OrderPriceSummary } from "components/global/components/OrderPriceSummary";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect, useRef } from "react";
import { selectIsReportingIssue, setIsReportingIssue } from "slices/appStateSlice";
import {
	selectActiveLocalTransaction,
	selectDiscountCodeState,
	selectIsEmptyOrder,
	selectOrderPaymentMethod,
	selectOrderSummary,
} from "slices/transactionSlice";
import { TransactionState, TransactionType } from "types";
import { ORDER_STEP_5_SMALL_ORDER_LIMIT } from "utils/designTokens";
import { PaymentMethodType } from "../../gql/graphql";
import { OrderDishesList } from "./components/OrderDishesList";
import { OrderListDiscountCodeItem } from "./components/OrderListDiscountCodeItem";
import { Order5DrawerHeader } from "./components/OrderStep5Header";
import { DrawerWrapper, OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";
import { RatingBox } from "./components/RatingBox";
import { EmptyOrderState } from "./EmptyOrderState";
import type { BaseOrderStepProps } from "./OrderStep2";

type OrderStep5Props = {
	resetOrder: () => void;
	doorHasBeenOpened: boolean;
};

export const OrderStep5 = ({
	open,
	resetOrder,
	toggleDrawer,
	doorHasBeenOpened,
}: BaseOrderStepProps & OrderStep5Props) => {
	const orderSummary = useAppSelector(selectOrderSummary);
	const discountCode = useAppSelector(selectDiscountCodeState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const isEmptyOrder = useAppSelector(selectIsEmptyOrder);
	const paymentMethodType = useAppSelector(selectOrderPaymentMethod);
	const {
		trackStockCheckEnded,
		trackFinishedOrderScreen,
		trackFinishedOrder,
		trackFinishedEmptyOrder,
		trackDoorNotOpened,
	} = useMixpanelTrackers();
	// Suspends the 10s auto-reset while the user is filling in ReportIssueModal so the
	// modal's own 20s countdown (or the user's choice) drives the close instead. The flag
	// lives in Redux so DrawerActionBar's AutolockTimer (a sibling, not a descendant) can
	// suspend itself the same way.
	const dispatch = useAppDispatch();
	const isReportingIssue = useAppSelector(selectIsReportingIssue);

	useEffect(() => {
		if (isReportingIssue) {
			return;
		}
		const timerId = setTimeout(() => {
			resetOrder();
		}, 10_000);
		return () => clearTimeout(timerId);
	}, [resetOrder, isReportingIssue]);

	// Track the finished-order screen and events at most once per transaction. Keyed by
	// transaction id — a boolean re-armed from the effect cleanup re-fired the events on any
	// dependency churn while the step stayed mounted (JID-2347); the id comparison keeps a
	// re-run inert for the same order and re-enables tracking when the next order arrives.
	// Deliberately never cleared: a re-delivered frame for the finished order must not re-fire.
	const lastTrackedTransactionIdRef = useRef<string | null>(null);
	useEffect(() => {
		// Only track if we have a transaction - otherwise wait for it
		if (!transaction) {
			return;
		}

		// Wait for the FINISHED frame before latching: the next customer's first frame can land
		// while the previous summary is still mounted, and door-never-opened orders enter step 5
		// at STOCK_CHECK — latching earlier would emit a wrong 0-meal $tablet_order_finished and
		// leave the registry suppressing the real one.
		if (transaction.state !== TransactionState.Finished) {
			return;
		}

		// A non-empty order can reach FINISHED before its summary is derived; latching then
		// would let trackFinishedOrder bail on the missing summary while the id guard
		// suppresses the retry — wait for the summary so the event is sent, not lost.
		if (transaction.type === TransactionType.Order && !(isEmptyOrder || orderSummary)) {
			return;
		}

		if (lastTrackedTransactionIdRef.current === transaction.id) {
			return;
		}
		lastTrackedTransactionIdRef.current = transaction.id;

		// Refills emit their own analytics from DeviceContext (trackRefillFinished); skip the
		// order-specific events here so a refill reaching step 5 doesn't double-track.
		if (transaction.type !== TransactionType.Order) {
			return;
		}

		trackStockCheckEnded(transaction.id);
		trackFinishedOrderScreen(isEmptyOrder, transaction.id);
		if (isEmptyOrder) {
			if (!doorHasBeenOpened) {
				trackDoorNotOpened(transaction.id);
			}
			trackFinishedEmptyOrder(transaction.id);
		} else {
			trackFinishedOrder(transaction.id, transaction, orderSummary);
		}
	}, [
		trackStockCheckEnded,
		trackFinishedOrderScreen,
		trackFinishedOrder,
		trackFinishedEmptyOrder,
		trackDoorNotOpened,
		isEmptyOrder,
		transaction,
		orderSummary,
		doorHasBeenOpened,
	]);

	const isTopUpPayment =
		paymentMethodType === PaymentMethodType.NativePayTopUp || paymentMethodType === PaymentMethodType.PaymentCardTopUp;

	const { orderDishTypes } = orderSummary || {};
	const drawerTopOffset = isTopUpPayment ? "240px" : "220px";

	return (
		<>
			<SwipeableArea orderstep={5} open={open}>
				{!open && <BlurredBackground orderStep={5} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Order5DrawerHeader isEmptyOrder={isEmptyOrder} paymentMethod={paymentMethodType} />
			</SwipeableArea>
			<DrawerWrapper
				sx={{
					...(open && {
						marginTop: drawerTopOffset,
						height: `calc(100% - ${drawerTopOffset})`,
					}),
				}}
			>
				{isEmptyOrder ? (
					<EmptyOrderState transactionId={transaction?.id || null} />
				) : (
					<OrderItemBlockWrapper
						sx={{
							// Keeps the band under the absolutely positioned RatingBox clear (120 + 144 + 8px gap).
							flex: 1,
							minHeight: 0,
							paddingBottom: "272px",
							// Nothing overflows this block; `auto` would only clip the discount code row's tint.
							overflow: "visible",
						}}
					>
						{orderSummary && orderDishTypes && (
							<>
								<OrderDishesWrapper
									maxHeight={
										orderDishTypes.length + (discountCode === null ? 0 : 1) > ORDER_STEP_5_SMALL_ORDER_LIMIT
											? "600px"
											: "320px"
									}
								>
									<OrderDishesList
										smallOrderLimit={ORDER_STEP_5_SMALL_ORDER_LIMIT}
										fitContent={discountCode !== null}
									/>
								</OrderDishesWrapper>

								<Box flexShrink={0} display="flex" flexDirection="column" gap={1}>
									<OrderListDiscountCodeItem discountCode={discountCode} isOrderFinished={true} />
									<OrderPriceSummary orderPrice={orderSummary.orderPrice?.total} />
								</Box>
							</>
						)}
						{transaction && !isEmptyOrder && (
							<RatingBox
								resetOrder={resetOrder}
								transactionId={transaction.id}
								onNegativeFeedbackClick={() => {
									dispatch(setIsReportingIssue(true));
								}}
							/>
						)}
					</OrderItemBlockWrapper>
				)}
			</DrawerWrapper>
		</>
	);
};
